import Quill, { type Range } from 'quill'
import Embed from 'quill/blots/embed'
import Delta, { type Op } from 'quill-delta'
import { useEffect, useRef } from 'react'
import { useFileUpload } from 'v2/shared/hooks/use-file-upload'
import 'quill/dist/quill.snow.css'
import { cn } from '@neo/lib/utils'
import { Label } from '@neo/primitives/label'
import { Tooltip } from '@neo/primitives/tooltip'
import { PaperclipIcon } from 'lucide-react'
import { truncateFileName } from 'v2/pages/conversations/utils'
import { logger } from 'v2/utils/logger'

// Extend Quill type to include insertNodeVar method
interface ExtendedQuill extends Quill {
  insertNodeVar: (nodevar: string) => void
  getCurrentCursorPosition: () => Range | null
  setCursorPosition: (index: number) => void
}

const ImageSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-image"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>`
const FileSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-file"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/></svg>`

// Custom Blot for file links
class FileBlot extends Embed {
  static blotName = 'file'
  static tagName = 'span'

  static create(value: { name: string; url: string }) {
    const node = document.createElement(FileBlot.tagName || 'span')
    node.setAttribute('data-file-name', value.name)
    node.setAttribute('data-file-url', value.url)
    node.setAttribute('contenteditable', 'false')
    node.setAttribute('class', 'py-1 px-1 !bg-hv1 !bg-hv2 text-xs-400 rounded-sm')

    // Create flex container
    const flexContainer = document.createElement('span')
    flexContainer.setAttribute('class', 'inline-flex items-center gap-1 relative top-1')
    flexContainer.setAttribute('contenteditable', 'false')

    // Create SVG container
    const svgContainer = document.createElement('span')
    svgContainer.setAttribute('class', 'h-4 w-4')

    // Check file type and set appropriate SVG
    const fileExtension = value.name?.split('.').pop()?.toLowerCase()
    const isImage = ['png', 'jpg', 'jpeg'].includes(fileExtension || '')
    svgContainer.innerHTML = isImage ? ImageSvg : FileSvg

    // Create text container
    const textContainer = document.createElement('span')
    // textContainer.setAttribute('class', '-translate-y-0.5')
    textContainer.textContent = truncateFileName(value.name || '', 25)

    // Append SVG and text to flex container
    flexContainer.appendChild(svgContainer)
    flexContainer.appendChild(textContainer)

    // Append flex container to node
    node.appendChild(flexContainer)

    return node
  }

  static formats(node: HTMLElement) {
    return {
      name: node.getAttribute('data-file-name'),
      url: node.getAttribute('data-file-url'),
    }
  }
}

// Register the custom Blot
Quill.register('formats/file', FileBlot)

interface EnhancedTextAreaProps {
  placeholder?: string
  onChange?: (content: string) => void
  value: string
  label?: string
  className?: string
  endIcon?: React.ReactNode
  disabled?: boolean
  name?: string
  showFileUpload?: boolean
  noFooter?: boolean
}

export const EnhancedTextArea = ({
  placeholder = 'Type your message...',
  onChange,
  value = '',
  className,
  label,
  endIcon,
  disabled = false,
  showFileUpload = true,
  name,
  noFooter = false,
}: EnhancedTextAreaProps) => {
  const editorRef = useRef<HTMLDivElement>(null)
  const quillRef = useRef<ExtendedQuill>()
  const { uploadFile } = useFileUpload((file) => file)

  // Convert Quill contents to markdown format
  const getMarkdownContent = (quill: Quill) => {
    const contents = quill.getContents()
    let markdown = ''

    contents.ops?.forEach((op: Op) => {
      // Handle string inserts
      if (typeof op.insert === 'string') {
        markdown += op.insert
        return
      }

      // Handle file inserts
      if (
        typeof op.insert === 'object' &&
        op.insert &&
        'file' in op.insert &&
        op.attributes &&
        typeof op.attributes === 'object' &&
        'name' in op.attributes &&
        'url' in op.attributes
      ) {
        const { name: fileName, url } = op.attributes as { name: string; url: string }
        // Only add valid file links
        if (
          fileName &&
          url &&
          fileName !== 'undefined' &&
          url !== 'undefined' &&
          fileName !== null &&
          url !== null
        ) {
          // Add a space before the link if there isn't one and we're not at the start
          if (markdown.length > 0 && !markdown.endsWith(' ') && !markdown.endsWith('\n')) {
            markdown += ''
          }
          markdown += `[${fileName}](${url})`
          // Add a space after the link
          markdown += ''
        }
      }
    })

    return markdown.trim()
  }

  // Parse markdown links and convert them to file blots
  const parseMarkdownLinks = (content: string) => {
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g
    let delta = new Delta()
    let lastIndex = 0
    let result = linkRegex.exec(content)

    while (result !== null) {
      const [fullMatch, fileName, fileUrl] = result
      const beforeText = content.slice(lastIndex, result.index)

      // Add text before the link
      if (beforeText) delta = delta.insert(beforeText)

      // Insert file blot without additional formatting
      delta = delta.insert({ file: { name: fileName, url: fileUrl } })
      delta = delta.insert(' ') // Add space after blot

      lastIndex = result.index + fullMatch.length
      result = linkRegex.exec(content)
    }

    // Add remaining text after last match
    const remainingText = content.slice(lastIndex)
    if (remainingText) delta = delta.insert(remainingText)

    return delta
  }

  const handleFileUpload = async (file: File) => {
    if (!quillRef.current) return

    const quill = quillRef.current
    const range = quill.getSelection() || { index: quill.getLength() - 1, length: 0 }
    const insertIndex = range.index

    try {
      // Insert loading placeholder
      quill.insertText(insertIndex, 'Uploading...', { color: '#666' })

      // Upload the file
      const result = await uploadFile(file)
      if (!result) {
        quill.deleteText(insertIndex, 'Uploading...'.length)
        return
      }

      // Remove loading text
      quill.deleteText(insertIndex, 'Uploading...'.length)

      // Insert the visual badge
      quill.insertEmbed(
        insertIndex,
        'file',
        {
          name: result.file_name,
          url: result.file_url,
        },
        'user',
      )

      // Insert a space after the badge
      quill.insertText(insertIndex + 1, ' ', 'user')

      // Move cursor after the inserted content
      quill.setSelection(insertIndex + 2, 0)

      // Trigger onChange with updated content
      const content = getMarkdownContent(quill)
      onChange?.(content)
    } catch (error) {
      // Remove loading text on error
      quill.deleteText(insertIndex, 'Uploading...'.length)
      logger.error('File upload failed:', error)
    }
  }

  const handleClick = () => {
    const input = document.createElement('input')
    input.setAttribute('type', 'file')
    input.setAttribute('accept', '*/*')
    input.setAttribute('data-testid', 'input-file-upload')
    input.setAttribute('multiple', 'false')
    input.style.display = 'none'
    document.body.appendChild(input)

    input.addEventListener('change', async () => {
      const file = input.files?.[0]
      if (!file) return

      try {
        await handleFileUpload(file)
      } finally {
        document.body.removeChild(input)
      }
    })

    input.click()
  }

  // Add method to get current cursor position
  const getCurrentCursorPosition = () => {
    const quill = quillRef.current
    if (!quill) return null
    return quill.getSelection()
  }

  // Add method to set cursor position
  const setCursorPosition = (index: number) => {
    const quill = quillRef.current
    if (!quill) return
    quill.setSelection(index, 0)
  }

  useEffect(() => {
    if (!editorRef.current) return

    const quill = new Quill(editorRef.current, {
      modules: {
        toolbar: false,
      },
      formats: ['file'],
      placeholder,
      theme: 'snow',
      readOnly: disabled,
    })

    // Handle text-change events
    quill.on('text-change', (_d, _o, source) => {
      // Only trigger onChange for user inputs, not programmatic changes
      if (source === 'user') {
        const content = getMarkdownContent(quill)
        onChange?.(content)
      }
    })

    // Handle selection changes
    quill.on('selection-change', (range) => {
      if (range) {
        // Store the current selection in a data attribute
        editorRef.current?.setAttribute('data-cursor-position', range.index.toString())
      }
    })

    quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
      const el = node as HTMLElement
      // Skip if the node is a root element to preserve line breaks
      if (el.tagName === 'DIV' || el.tagName === 'P') return delta

      // Get plain text and preserve whitespace
      const plaintext = el.textContent?.replace(/\s+/g, ' ').trim()
      if (plaintext) {
        return new Delta().insert(plaintext)
      }
      return new Delta()
    })

    // Add method to insert nodevar at cursor position
    const insertNodeVar = (nodevar: string) => {
      // Try to get the stored cursor position first
      const storedPosition = editorRef.current?.getAttribute('data-cursor-position')
      const range = quill.getSelection() ||
        (storedPosition ? { index: parseInt(storedPosition, 10), length: 0 } : null) || {
          index: 0,
          length: 0,
        }

      const insertIndex = range.index

      // Remove the last '{{' if present
      const text = quill.getText()
      if (text.trim().slice(-2) === '{{') {
        quill.deleteText(text.length - 3, 3)
      }

      // Insert the nodevar at cursor position
      quill.insertText(insertIndex, nodevar, 'user')
      quill.setSelection(insertIndex + nodevar.length, 0)

      // Trigger onChange with updated content
      const content = getMarkdownContent(quill)
      onChange?.(content)
    }

    // Expose methods through ref
    const extendedQuill = Object.assign(quill, {
      insertNodeVar,
      getCurrentCursorPosition,
      setCursorPosition,
    }) as ExtendedQuill

    quillRef.current = extendedQuill

    // Make the Quill instance findable by Quill.find
    editorRef.current.setAttribute('data-quill-editor', 'true')

    return () => {
      quillRef.current = undefined
      if (editorRef.current) {
        editorRef.current.removeAttribute('data-quill-editor')
        editorRef.current.removeAttribute('data-cursor-position')
      }
    }
  }, [])

  useEffect(() => {
    const quill = quillRef.current
    if (!quill) return

    quill.enable(!disabled)
  }, [disabled])

  useEffect(() => {
    const quill = quillRef.current
    if (!quill || value === undefined) return

    const currentContent = getMarkdownContent(quill)
    if (currentContent !== value) {
      const delta = parseMarkdownLinks(value)
      quill.setContents(delta, 'silent')
    }
  }, [value])

  return (
    <div className="relative flex flex-col gap-1.5" id={name}>
      {label && <Label>{label}</Label>}
      <div
        className={cn(
          'w-full rounded-md border border-st1 bg-background relative min-h-[120px]',
          'focus-within:outline-none focus-within:ring-2 focus-within:ring-ring',
          '[&_.ql-container.ql-snow]:border-0 [&_.ql-container.ql-snow]:text-p-400',
          '[&_.ql-editor]:break-all',
          '[&_.ql-editor.ql-blank::before]:not-italic [&_.ql-editor.ql-blank::before]:text-muted-foreground [&_.ql-editor.ql-blank::before]:opacity-85',
          'flex flex-col overflow-hidden resize-y',
          disabled && 'opacity-50',
          className,
        )}
        data-testid="input-enhanced-textarea"
        role="textbox"
        aria-label={label}
      >
        <div ref={editorRef} className="h-full ql-ref flex-1 flex flex-col" />
        {!noFooter && (
          <div className="p-2 flex gap-2 mt-auto justify-end">
            {showFileUpload && (
              <Tooltip content="Attach file">
                <button
                  onClick={handleClick}
                  className="ml-auto"
                  type="button"
                  aria-label="Attach file"
                  data-testid="button-file-upload"
                  disabled={disabled}
                  title={disabled ? 'File upload is disabled' : ''}
                >
                  <PaperclipIcon
                    className={cn('h-4 w-4 text-tertiary', disabled && 'cursor-not-allowed')}
                  />
                </button>
              </Tooltip>
            )}
            {endIcon}
          </div>
        )}
      </div>
    </div>
  )
}
